// native
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, mergeMap, tap } from 'rxjs/operators';

// addon
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

// service
import { PatientsService } from 'app/core/services/patients.service';
import { DevicesService } from 'app/core/services/devices.service';
import { ErrorService } from 'app/core/services/error.service';
import { HomeManagementService } from '../home-management.service';
import { HomeManagementStateService } from '../home-management-state.service';

// models
import { CenterContainerTitle, Device, HomeSetupRequest, Patient } from '../../models';
import { DeviceStepResult, NetworkStepResult, PatientStepResult } from '../home-management.types';

@Component({
  selector: 'app-home-management-wizard',
  templateUrl: './home-management-wizard.component.html',
  standalone: false
})
export class HomeManagementWizardComponent implements OnInit {
  readonly totalSteps = 5;
  currentStep = 1;

  patient: PatientStepResult | null = null;
  device: DeviceStepResult | null = null;
  network: NetworkStepResult | null = null;

  // Pairing code shown on the final step; known only after the device exists
  // (created on submit for a new device, already set for an existing one).
  pairingCode: string | null = null;

  isSubmitting = false;

  title: CenterContainerTitle;

  // Final step shows a pairing code for new / unlinked devices. An existing
  // device that is already linked cannot be paired again, so the step shows
  // a generic success message instead.
  get deviceNeedsLinking(): boolean {
    return !(this.device?.mode === 'existing' && !!this.device.device?.user);
  }

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private patientsService: PatientsService,
    private devicesService: DevicesService,
    private homeManagementService: HomeManagementService,
    private errorService: ErrorService,
    private toastService: ToastrService,
    private stateService: HomeManagementStateService
  ) { }

  ngOnInit(): void {
    // Resolve the title once translations are loaded; using instant() in the
    // constructor can return the raw key before the i18n bundle is ready.
    this.translateService.get('homeManagement').subscribe(value => this.title = { bold: value });

    // Resume where the user left off if they navigated away and came back
    // within the persistence window (same browser).
    const saved = this.stateService.load();
    if (saved) {
      this.currentStep = saved.currentStep;
      this.patient = saved.patient;
      this.device = saved.device;
      this.network = saved.network;
    }
  }

  private persist(): void {
    this.stateService.save({
      currentStep: this.currentStep,
      patient: this.patient,
      device: this.device,
      network: this.network
    });
  }

  onPatientNext(result: PatientStepResult): void {
    this.patient = result;
    this.currentStep = 2;
    this.persist();
  }

  onDeviceNext(result: DeviceStepResult): void {
    this.device = result;
    this.currentStep = 3;
    this.persist();
  }

  onDeviceBack(): void {
    this.currentStep = 1;
    this.persist();
  }

  onNetworkNext(result: NetworkStepResult): void {
    this.network = result;
    this.currentStep = 4;
    this.persist();
  }

  onNetworkBack(): void {
    this.currentStep = 2;
    this.persist();
  }

  onReviewBack(): void {
    this.currentStep = 3;
    this.persist();
  }

  onSubmit(): void {
    if (!this.patient || !this.device || !this.network)
      return;

    this.isSubmitting = true;

    // The device is created here (not in step 2) for the "create new" flow, so
    // nothing is persisted in the system until the user finishes the wizard.
    // Order: device -> patient -> home setup, rolling back in reverse if a
    // later call fails so we never leave orphan records behind.
    this.resolveDevice().pipe(
      mergeMap((device: Device) =>
        this.resolvePatient().pipe(
          // If patient creation fails, roll back the just-created device.
          catchError(error => {
            this.rollbackDevice(device);
            return throwError(error);
          }),
          mergeMap((patient: Patient) =>
            this.homeManagementService.create(this.buildSetupBody(patient.id, device.id)).pipe(
              // If the setup fails, roll back the just-created patient/device.
              catchError(error => {
                this.rollbackPatient(patient);
                this.rollbackDevice(device);
                return throwError(error);
              })
            )
          )
        )
      ),
      finalize(() => this.isSubmitting = false)
    ).subscribe(() => {
      this.stateService.clear();
      this.toastService.success(this.translateService.instant('homeSetupCreateSuccess'));
      // Show the device code on the final step instead of leaving immediately.
      this.currentStep = this.totalSteps;
    }, error => this.errorService.handleError(error?.error));
  }

  // Resolves the device for the setup. For an existing device it is returned
  // as-is; for a new device it is created now and re-fetched to obtain its
  // pairing code (the create response omits it). Already-linked existing
  // devices skip the pairing code — re-linking is not possible.
  private resolveDevice(): Observable<Device> {
    if (this.device.mode === 'new') {
      return this.devicesService.create(this.device.newDevice).pipe(
        mergeMap((created: Device) => this.devicesService.getOne(created.id)),
        tap((full: Device) => this.pairingCode = full.code)
      );
    }

    // Keep in sync with deviceNeedsLinking — do not re-check device.user here.
    this.pairingCode = this.deviceNeedsLinking
      ? (this.device.device.code ?? this.device.pairingCode ?? null)
      : null;
    return of(this.device.device);
  }

  // Resolves the patient: reuses an existing (duplicate) patient when chosen,
  // otherwise creates a new one.
  private resolvePatient(): Observable<Patient> {
    return this.patient.existingPatient
      ? of(this.patient.existingPatient)
      : this.patientsService.create(this.patient.request);
  }

  private rollbackDevice(device: Device): void {
    if (this.device.mode === 'new' && device?.id)
      this.devicesService.delete(device.id).subscribe(() => { }, () => { });
  }

  private rollbackPatient(patient: Patient): void {
    if (!this.patient.existingPatient && patient?.id)
      this.patientsService.delete(patient.id).subscribe(() => { }, () => { });
  }

  private buildSetupBody(patientId: number, deviceId: number): HomeSetupRequest {
    return {
      patient: patientId,
      device: deviceId,
      phone_os: this.network.phone_os,
      hotspot_enabled: this.network.hotspot_enabled,
      wifi_ssid: this.network.wifi_ssid,
      wifi_password: this.network.wifi_password,
      cancel_pending_schedules: this.device.reallocate
    };
  }

  close(): void {
    this.router.navigate(['/patients']);
  }
}
